/**
 * The languages AVEO speaks.
 *
 * German is the default and the source language: every key exists in de.json
 * first, and a test holds the other files against it so a missing translation is
 * caught here rather than showing up as a raw key in the interface.
 *
 * Portuguese is not decoration — thirteen of the accounts in the directory are
 * @avex.pt.
 *
 * Free of React, Next and next-intl on purpose: this is read from the request
 * config, from server components and from the client, and it must not drag a
 * runtime into any of them.
 */
export const LOCALES = ['de', 'en', 'pt'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'de'

/** Name of the cookie that carries a deliberate choice. */
export const LOCALE_COOKIE = 'aveo_locale'

/** What the switcher shows, each in its own language. */
export const LOCALE_LABELS: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  pt: 'Português'
}

export function isLocale(value: string | null | undefined): value is Locale {
  return value != null && (LOCALES as readonly string[]).includes(value)
}

/**
 * Which language to render in.
 *
 * The cookie wins because it is the only deliberate choice in play: somebody
 * picked a language in the switcher. Accept-Language is a guess the browser
 * makes, so it only decides for a visitor who has never chosen.
 *
 * Pure and testable; the cookie and the header are read by the caller.
 */
export function resolveLocale(
  cookieValue: string | null | undefined,
  acceptLanguage: string | null | undefined
): Locale {
  if (isLocale(cookieValue)) return cookieValue
  return fromAcceptLanguage(acceptLanguage) ?? DEFAULT_LOCALE
}

/**
 * First supported language in an Accept-Language header, by the browser's own
 * order of preference.
 *
 * Deliberately simple: the quality values are honoured by ORDER, not by parsing
 * q= — browsers send them in descending order, and a hand-written header that
 * does not is not worth a parser. Region subtags are dropped, so 'pt-BR' and
 * 'pt-PT' both land on 'pt'.
 */
function fromAcceptLanguage(header: string | null | undefined): Locale | null {
  if (header == null) return null
  for (const part of header.split(',')) {
    const tag = part.split(';')[0].trim().toLowerCase()
    const base = tag.split('-')[0]
    if (isLocale(base)) return base
  }
  return null
}

/**
 * The language of one request, given how it was let in.
 *
 * A screen behind a dashboard token or the IP list (TV, Yodeck) has no account
 * and nobody in front of it to choose. Its browser's Accept-Language says
 * nothing about the people looking at it, so it reads German unless a language
 * was picked on that device (owner's decision, 2026-10-16). Everybody else
 * resolves as in resolveLocale.
 */
export function resolveRequestLocale(
  cookieValue: string | null | undefined,
  acceptLanguage: string | null | undefined,
  accessMethod: string | null | undefined
): Locale {
  if (accessMethod === 'token' || accessMethod === 'ip') return resolveLocale(cookieValue, null)
  return resolveLocale(cookieValue, acceptLanguage)
}
